import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import close from '../../../../assets/icons/close.svg';
import Notifier from '../../../../Utils/notify';
import {
  getReconciliationLedger,
  submitReconciliationReport,
  friendlyReconciliationError,
} from '../../../../API/reconciliationApi';
import { REPORT_TYPE_LABELS } from '../../../../Utils/permissionCodes';
import '../../../../assets/css/Modal.css';

const fmtMoney = (v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : `₹${Number(v).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

export default function SubmitReportModal({
  isOpen,
  onClose,
  studentId,
  studentName,
  activityId,
  reportType, // 'accounts' | 'library'
  onSuccess,
}) {
  const [ledger, setLedger] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [pin, setPin] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const pinInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setPin('');
      setRemarks('');
      setLedger(null);
      return;
    }

    let cancelled = false;
    setLoadingLedger(true);
    getReconciliationLedger({ studentId, activityId })
      .then((res) => {
        if (cancelled) return;
        if (res?.error) {
          Notifier.error(friendlyReconciliationError(res.error));
          onClose();
          return;
        }
        setLedger(res);
      })
      .finally(() => !cancelled && setLoadingLedger(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, studentId, activityId, onClose]);

  useEffect(() => {
    if (isOpen && !loadingLedger) {
      const t = setTimeout(() => pinInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen, loadingLedger]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (pin.length !== 4) {
      Notifier.error('PIN must be 4 digits');
      return;
    }

    const payload = { student_id: studentId, report_type: reportType, pin, remarks };
    // For accounts the activity context is the report's scope; for library it's
    // a hint so the backend can refuse if the admit card has already been issued.
    if (activityId) payload.activity_id = activityId;

    try {
      setSubmitting(true);
      const res = await submitReconciliationReport(payload);
      if (res?.error) {
        Notifier.error(friendlyReconciliationError(res.error));
        if (res?.existing_report) onSuccess?.(res.existing_report); // refresh row to reflect existing
        return;
      }
      Notifier.success('Reconciliation report submitted');
      onSuccess?.(res?.report);
      onClose();
    } catch (e) {
      console.error('submitReconciliationReport failed', e);
      Notifier.error('Could not submit report. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const reportLabel = REPORT_TYPE_LABELS[reportType] || reportType;

  return createPortal(
    <div className="modal-overlay" style={{ zIndex: 9999999 }}>
      <div
        className="modal-content"
        style={{
          position: 'relative',
          width: '480px',
          maxWidth: '92vw',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div
          className="modal-header-content"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h2 style={{ margin: 0, color: '#7F56DA', fontSize: '18px' }}>
            Submit {reportLabel} Reconciliation Report
          </h2>
          <button className="close-btn" onClick={onClose}>
            <img src={close} alt="close" />
          </button>
        </div>

        <div style={{ fontSize: 13, color: '#6B778C' }}>
          Student: <strong style={{ color: '#222F3E' }}>{studentName}</strong>
        </div>

        <div
          style={{
            background: '#F9FAFB',
            border: '1px dashed #d3d3e8',
            borderRadius: 8,
            padding: '12px 14px',
          }}
        >
          {loadingLedger ? (
            <div style={{ color: '#6B778C', fontSize: 13 }}>Loading…</div>
          ) : ledger ? (
            reportType === 'accounts' ? (
              <>
                <div style={{ fontSize: 12, color: '#6B778C', marginBottom: 8 }}>
                  Review the student's current fee ledger and confirm it is in order.
                  Your sign-off does <strong>not</strong> change the ledger — it
                  only records that you have verified it.
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    rowGap: 6,
                    columnGap: 16,
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: '#6B778C' }}>Total Credit</span>
                  <strong>{fmtMoney(ledger?.ledger?.credit_total)}</strong>
                  <span style={{ color: '#6B778C' }}>Total Debit</span>
                  <strong>{fmtMoney(ledger?.ledger?.debit_total)}</strong>
                  <span style={{ color: '#6B778C' }}>Pending Amount</span>
                  <strong style={{ color: Number(ledger?.ledger?.pending_amount) > 0 ? '#d4380d' : '#16a34a' }}>
                    {fmtMoney(ledger?.ledger?.pending_amount)}
                  </strong>
                </div>
                {ledger?.accounts_report && (
                  <div style={{ marginTop: 10, color: '#d4380d', fontSize: 12 }}>
                    An accounts report already exists for this student in this activity.
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, color: '#6B778C', marginBottom: 8 }}>
                  Review the student's current Library NOC status and confirm it is in order.
                  Your sign-off does <strong>not</strong> change the Library NOC —
                  it only records that you have verified it.
                </div>
                <div style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6B778C' }}>Current Library NOC status</span>
                  <strong style={{ color: ledger?.library_noc_status === 'COMPLETED' ? '#16a34a' : '#d4380d' }}>
                    {ledger?.library_noc_status || '—'}
                  </strong>
                </div>
                {ledger?.library_report && (
                  <div style={{ marginTop: 10, color: '#d4380d', fontSize: 12 }}>
                    A library report already exists for this student.
                  </div>
                )}
              </>
            )
          ) : (
            <div style={{ color: '#d4380d', fontSize: 13 }}>Could not load report context.</div>
          )}
        </div>

        <div>
          <label className="form-labell">Security PIN</label>
          <input
            ref={pinInputRef}
            type="password"
            inputMode="numeric"
            maxLength={4}
            className="form-control"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter your 4-digit PIN"
            autoComplete="off"
          />
        </div>

        <div>
          <label className="form-labell">Remarks (optional)</label>
          <textarea
            className="form-control"
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Any notes for the audit log"
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            className="add-btn"
            style={{
              padding: '10px 16px',
              background: '#fff',
              color: '#222F3E',
              border: '1px solid #d3d3e8',
              borderRadius: 8,
              cursor: 'pointer',
            }}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="add-btn"
            style={{
              padding: '10px 16px',
              background:
                pin.length === 4 && !submitting ? '#7F56DA' : '#d3d3d3',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor:
                pin.length === 4 && !submitting ? 'pointer' : 'not-allowed',
              fontWeight: 600,
            }}
            disabled={pin.length !== 4 || submitting || loadingLedger}
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
